import { Button, Input, Popconfirm, Select, Space, Table } from "antd";
import {
	DeleteOutlined,
	DownloadOutlined,
	EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	deleteEmployeeDocument,
	loadAllEmployeeDocument,
} from "../../redux/rtk/features/employeeDocument/employeeDocumentSlice";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";

function GetAllEmployeeDocuments({
	form,
	setIsEditMode,
	setCurrentDocumentId,
	mode = "admin",
	loggedInEmployeeId,
}) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const [searchEmployee, setSearchEmployee] = useState("");
	const [searchDocumentName, setSearchDocumentName] = useState("");
	const [filterEmployeeId, setFilterEmployeeId] = useState("");
	const list = useSelector((state) => state.employeeDocument?.list || []);
	const loading = useSelector((state) => state.employeeDocument?.loading || false);
	const staffList = useSelector((state) => state.users?.list || []);
	const isUserView = mode === "user";

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	useEffect(() => {
		if (isUserView && loggedInEmployeeId != null) {
			dispatch(loadAllEmployeeDocument({ mode: "user", employeeId: loggedInEmployeeId }));
		} else if (!isUserView) {
			dispatch(loadAllEmployeeDocument({ mode: "admin" }));
		}
	}, [dispatch, isUserView, loggedInEmployeeId]);

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => (e.EmployeeId ?? e.id) === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName ||
				""
			);
		}
		return employeeId != null ? String(employeeId) : "";
	};

	const DOCUMENT_NAME_OPTIONS = [
		"Offer Letter", "Appointment Letter", "Experience Certificate",
		"Relieving Letter", "Salary Certificate", "NOC (No Objection Certificate)",
		"Aadhaar", "PAN", "10 certificate", "12th certificate", "Other",
	];

	const handleEdit = (record) => {
		const docName = (record.DocumentName || record.documentName || record.documentType || record.DocumentType || "").trim();
		const presetMatch = DOCUMENT_NAME_OPTIONS.find(
			(opt) => String(opt).toLowerCase() === docName.toLowerCase()
		);
		const isPreset = Boolean(presetMatch);
		const selectedDocumentName = isPreset ? presetMatch : "Other";
		setIsEditMode(true);
		setCurrentDocumentId(record.id || record.EmployeeDocumentId || record.DocumentId);
		form.setFieldsValue({
			employeeId: record.employeeId || record.EmployeeId,
			documentNameSelect: selectedDocumentName,
			documentNameOther: isPreset ? undefined : docName,
			issueDate: record.IssueDate || record.issueDate ? dayjs(record.IssueDate || record.issueDate) : null,
			expiryDate: record.ExpiryDate || record.expiryDate ? dayjs(record.ExpiryDate || record.expiryDate) : null,
			remarks: record.Remarks || record.remarks || record.Notes || record.notes,
		});
	};

	const handleDelete = async (id) => {
		const resp = await dispatch(deleteEmployeeDocument(id));
		if (resp.payload?.message === "success") {
			if (isUserView && loggedInEmployeeId != null) {
				dispatch(loadAllEmployeeDocument({ mode: "user", employeeId: loggedInEmployeeId }));
			} else {
				dispatch(loadAllEmployeeDocument({ mode: "admin" }));
			}
		}
	};

	const documentUrl = (record) =>
		record.UploadDocumentUrl ?? record.uploadDocumentUrl ?? record.UploadDocument ?? record.uploadDocument;

	const handleDownload = (record) => {
		const url = documentUrl(record);
		if (!url) return;
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", (record.DocumentName ?? record.documentName ?? "document") + ".pdf");
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		document.body.appendChild(link);
		link.click();
		link.remove();
	};

	const columns = [
		{
			id: 1,
			title: "S.No",
			dataIndex: "serial",
			key: "serial",
		},
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Document Name",
			dataIndex: "documentName",
			key: "documentName",
			render: (_, record) => record.DocumentName || record.documentName || record.documentType || record.DocumentType || "-",
		},
		{
			title: "Issue Date",
			dataIndex: "IssueDate",
			key: "issueDate",
			render: (_, record) => {
				const d = record.IssueDate ?? record.issueDate;
				return d ? dayjs(d).format("DD-MM-YYYY") : "-";
			},
		},
		{
			title: "Expiry Date",
			dataIndex: "ExpiryDate",
			key: "expiryDate",
			render: (_, record) => {
				const d = record.ExpiryDate ?? record.expiryDate;
				return d ? dayjs(d).format("DD-MM-YYYY") : "-";
			},
		},
		{
			title: "Actions",
			key: "action",
			render: (_, record) => {
				const id = record.id || record.EmployeeDocumentId || record.DocumentId;
				return (
					<Space>
						<Button
							type="link"
							icon={<DownloadOutlined />}
							onClick={() => handleDownload(record)}
							title="Download"
						/>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => handleEdit(record)}
						/>
						<Popconfirm
							title="Are you sure you want to delete this document?"
							onConfirm={() => handleDelete(id)}
							okText="Yes"
							cancelText="No"
						>
							<Button type="link" danger icon={<DeleteOutlined />} />
						</Popconfirm>
					</Space>
				);
			},
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || i.EmployeeDocumentId || i.DocumentId || index,
			serial: index + 1,
			employeeName: i.EmployeeName ?? mapEmployeeName(i.employeeId || i.EmployeeId),
		}));

	const filteredData = addKeys(list).filter((item) => {
		if (!isUserView && filterEmployeeId !== "" && filterEmployeeId != null) {
			const itemEmpId = item.employeeId ?? item.EmployeeId;
			if (String(itemEmpId) !== String(filterEmployeeId)) return false;
		}
		const employeeName = String(item.employeeName ?? "").toLowerCase();
		const docName = String(item.DocumentName ?? item.documentName ?? item.documentType ?? "").toLowerCase();
		const matchesEmployee = employeeName.includes(String(searchEmployee ?? "").toLowerCase());
		const matchesDoc = docName.includes(String(searchDocumentName ?? "").toLowerCase());
		return matchesEmployee && matchesDoc;
	});

	return (
		<div className="employee-document-table-container">
			<div className="text-center my-2 flex justify-between items-center">
				<h5 className="text-color-2 text-xl mb-2">Employee Documents List</h5>
				{list && (
					<div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
						{!isUserView && (
							<Select
								placeholder="Filter by employee"
								allowClear
								style={{ width: 220 }}
								value={filterEmployeeId}
								onChange={(v) => setFilterEmployeeId(v ?? "")}
								options={[
									{ value: "", label: "All employees" },
									...(staffList || [])
										.filter((e) => (e.EmployeeId ?? e.id) != null)
										.map((e) => ({
											value: e.EmployeeId ?? e.id,
											label: e.EmployeeName || `${e.firstName || ""} ${e.lastName || ""}`.trim() || e.UserName || `Employee ${e.EmployeeId ?? e.id}`,
										})),
								]}
							/>
						)}
						<ColVisibilityDropdown
							options={columns}
							columns={columns}
							columnsToShowHandler={columnsToShowHandler}
						/>
						<Input
							placeholder="Search by Employee"
							value={searchEmployee}
							onChange={(e) => setSearchEmployee(e.target.value)}
							style={{ width: 200 }}
						/>
						<Input
							placeholder="Search by Document Name"
							value={searchDocumentName}
							onChange={(e) => setSearchDocumentName(e.target.value)}
							style={{ width: 200 }}
						/>
					</div>
				)}
			</div>

			<Table
				scroll={{ x: true }}
				loading={loading}
				columns={columnsToShow}
				dataSource={filteredData}
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} of ${total} items`,
					pageSizeOptions: ["10", "20", "50", "100"],
				}}
			/>
		</div>
	);
}

export default GetAllEmployeeDocuments;

